import React from 'react'
import {useTranslation} from '@lib/intl/useTranslations'

import {PORTFOLIO_REFRESH_INTERVAL, PRO_PORTFOLIO_REFRESH_INTERVAL} from '@constant/refreshInterval'

import useFormatNumber from '@hook/useFormatNumber'
import useDark from '@hook/useDark'
import useQueryFetchMe from '@hook/query/account/useQueryFetchMe'
import useQueryGetUserProfile from '@hook/query/portfolio/useQueryGetUserProfile'

import Text from '@component/Text/Text'
import WizImage from '@component/images/WizImage'
import TagText from '@component/Text/TagText'
import WizTooltip from '@component/tooltip/WizTooltip'
import ThreeColorStack from '@feature/portfolio/components/ThreeColorStack/ThreeColorStack'

import IconInfoDark from '@svg/common/ic_info_dark.svg'
import Info from '@svg/common/ic_info.svg'
import img_sharp_calc_dark from '@image/img_sharp_calc_dark.png'
import img_sharp_calc from '@image/img_sharp_calc.png'

interface IProps {
    className?: string
    id?: string
    login_id?: string
}

const MobileUserSharpRatioComponent = ({className, id, login_id}: IProps) => {
    const t = useTranslation()
    const {displayNumber} = useFormatNumber()
    const {isRenderDark} = useDark()

    const {data: me} = useQueryFetchMe(undefined, false)
    const {data: profile} = useQueryGetUserProfile(
        id,
        me?.is_pro && id === login_id ? PRO_PORTFOLIO_REFRESH_INTERVAL : PORTFOLIO_REFRESH_INTERVAL,
        false,
    )
    return (
        <div className={`${className}`}>
            <div className={'flex items-center'}>
                <Text className={'text-body_comm text-gray02 dark:text-dark_gray02'}>
                    {t('balanceHeader.stats.sharpeRatio.title')}
                </Text>
                <WizTooltip
                    type={'portfolio_sharpe_ratio'}
                    plusX={-10}
                    placement={'bottom'}
                    title={t('portfolio.dashboard.profitGraph.selector.type.sharpeRatio.tooltip.title')}
                    render={
                        isRenderDark ? (
                            <IconInfoDark className={'w-[10px] fill-bg_dark_white03 hover:fill-bg_dark_white01'} />
                        ) : (
                            <Info className={'w-[10px] fill-white hover:fill-gray08'} />
                        )
                    }>
                    <Text className={'text-body3 text-gray02 dark:text-dark_gray02 mt-[5px]'}>
                        {t('portfolio.dashboard.profitGraph.selector.type.sharpeRatio.tooltip.text')}
                    </Text>
                    <WizImage
                        src={isRenderDark ? img_sharp_calc_dark : img_sharp_calc}
                        containerClassName={'max-w-[170px] mx-auto py-[20px]'}
                    />
                    <TagText className={'text-body3 text-gray02 dark:text-dark_gray02 '}>
                        {t('portfolio.dashboard.profitGraph.selector.type.sharpeRatio.tooltip.text2')}
                    </TagText>

                    <Text className={'pt-[30px] text-ti2 text-gray01 dark:text-dark_gray01'}>
                        {t('portfolio.dashboard.profitGraph.selector.type.accuracy.tooltip.title')}
                    </Text>
                    <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pt-[10px]'}>
                        {t('portfolio.dashboard.profitGraph.selector.type.accuracy.tooltip.text')}
                    </Text>
                    <div className={'pt-[10px] flex items-center justify-between'}>
                        <div className={'flex items-center px-[5px]'}>
                            <Text className={'text-[13px] font-bold text-gray02 dark:text-dark_gray02'}>
                                {t('portfolio.dashboard.profitGraph.selector.type.accuracy.row')}
                            </Text>
                            <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pl-[7px] pr-[4px]'}>
                                {t('balanceHeader.stats.sharpeRatio.accuracy')}
                            </Text>
                            <ThreeColorStack charge_low={true} />
                        </div>
                        <div className={'flex items-center px-[5px]'}>
                            <Text className={'text-[13px] font-bold text-gray02 dark:text-dark_gray02'}>
                                {t('portfolio.dashboard.profitGraph.selector.type.accuracy.fair')}
                            </Text>
                            <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pl-[7px] pr-[4px]'}>
                                {t('balanceHeader.stats.sharpeRatio.accuracy')}
                            </Text>
                            <ThreeColorStack charge_fair={true} />
                        </div>

                        <div className={'flex items-center px-[5px]'}>
                            <Text className={'text-[13px] font-bold text-gray02 dark:text-dark_gray02'}>
                                {t('portfolio.dashboard.profitGraph.selector.type.accuracy.high')}
                            </Text>
                            <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pl-[7px] pr-[4px]'}>
                                {t('balanceHeader.stats.sharpeRatio.accuracy')}
                            </Text>
                            <ThreeColorStack charge_fair={true} charge_high={true} />
                        </div>
                    </div>
                </WizTooltip>
            </div>
            <div className={'flex items-center gap-x-[3px]'}>
                <Text className={`text-ti2 text-gray01 dark:text-dark_gray01`}>
                    {profile?.analytics?.sharpe_ratio
                        ? profile?.analytics?.sharpe_ratio?.sharpe_ratio
                            ? displayNumber(profile?.analytics?.sharpe_ratio?.sharpe_ratio, 2)
                            : '0.00'
                        : '-'}
                </Text>
                {profile?.analytics?.sharpe_ratio && (
                    <WizTooltip
                        type={'portfolio_sharpe_ratio'}
                        plusX={-10}
                        placement={'bottom'}
                        title={t('portfolio.dashboard.profitGraph.selector.type.sharpeRatio.tooltip.title')}
                        render={
                            <div className={'bg-white dark:bg-bg_dark_white02 px-[5px] py-[3px] rounded-[3px]'}>
                                <ThreeColorStack
                                    charge_low={profile?.analytics?.sharpe_ratio?.accuracy === 0}
                                    charge_fair={profile?.analytics?.sharpe_ratio?.accuracy > 0}
                                    charge_high={profile?.analytics?.sharpe_ratio?.accuracy > 1}
                                />
                            </div>
                        }>
                        <Text className={'text-body3 text-gray02 dark:text-dark_gray02 mt-[5px]'}>
                            {t('portfolio.dashboard.profitGraph.selector.type.sharpeRatio.tooltip.text')}
                        </Text>
                        <WizImage
                            src={isRenderDark ? img_sharp_calc_dark : img_sharp_calc}
                            containerClassName={'max-w-[170px] mx-auto py-[20px]'}
                        />
                        <TagText className={'text-body3 text-gray02 dark:text-dark_gray02 '}>
                            {t('portfolio.dashboard.profitGraph.selector.type.sharpeRatio.tooltip.text2')}
                        </TagText>

                        <Text className={'pt-[30px] text-ti2 text-gray01 dark:text-dark_gray01'}>
                            {t('portfolio.dashboard.profitGraph.selector.type.accuracy.tooltip.title')}
                        </Text>
                        <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pt-[10px]'}>
                            {t('portfolio.dashboard.profitGraph.selector.type.accuracy.tooltip.text')}
                        </Text>
                        <div className={'pt-[10px] flex items-center justify-between'}>
                            <div className={'flex items-center px-[5px]'}>
                                <Text className={'text-[13px] font-bold text-gray02 dark:text-dark_gray02'}>
                                    {t('portfolio.dashboard.profitGraph.selector.type.accuracy.row')}
                                </Text>
                                <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pl-[7px] pr-[4px]'}>
                                    {t('balanceHeader.stats.sharpeRatio.accuracy')}
                                </Text>
                                <ThreeColorStack charge_low={true} />
                            </div>
                            <div className={'flex items-center px-[5px]'}>
                                <Text className={'text-[13px] font-bold text-gray02 dark:text-dark_gray02'}>
                                    {t('portfolio.dashboard.profitGraph.selector.type.accuracy.fair')}
                                </Text>
                                <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pl-[7px] pr-[4px]'}>
                                    {t('balanceHeader.stats.sharpeRatio.accuracy')}
                                </Text>
                                <ThreeColorStack charge_fair={true} />
                            </div>

                            <div className={'flex items-center px-[5px]'}>
                                <Text className={'text-[13px] font-bold text-gray02 dark:text-dark_gray02'}>
                                    {t('portfolio.dashboard.profitGraph.selector.type.accuracy.high')}
                                </Text>
                                <Text className={'text-body3 text-gray02 dark:text-dark_gray02 pl-[7px] pr-[4px]'}>
                                    {t('balanceHeader.stats.sharpeRatio.accuracy')}
                                </Text>
                                <ThreeColorStack charge_fair={true} charge_high={true} />
                            </div>
                        </div>
                    </WizTooltip>
                )}
            </div>
        </div>
    )
}
export default MobileUserSharpRatioComponent
